import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Columns3, List, Plus, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pager, SelectField, useAssignees, useLookups } from "@/lib/crm";
import { usePipeline } from "@/lib/sales";
import { DemoBadge, PageHeader, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PipelineBoard } from "@/components/sales/PipelineBoard";
import { OpportunityTable } from "@/components/sales/OpportunityTable";
import { OpportunityDialog } from "@/components/sales/OpportunityDialog";

const EMPTY = { q: "", owner_id: "", stage_id: "", status: "", source: "", value_min: "", value_max: "", close_from: "", close_to: "", next_action: "", archived: false, sort: "updated_at", dir: "desc", page: 1 };
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null && v !== false));

const ListFilters = ({ f, set, lk, stages }) => (
  <Popover>
    <PopoverTrigger asChild><Button variant="outline" className="gap-2" data-testid="opp-more-filters"><SlidersHorizontal />Filtri</Button></PopoverTrigger>
    <PopoverContent align="end" className="w-80 space-y-3">
      <SelectField label="Fase" value={f.stage_id} onChange={(v) => set({ stage_id: v })} options={stages.map((s) => ({ id: s.id, name: s.name }))} allowNone="Tutte" testId="opp-filter-stage" />
      <SelectField label="Stato" value={f.status} onChange={(v) => set({ status: v })} allowNone="Tutti" testId="opp-filter-status"
        options={[{ key: "open", label: "Aperte" }, { key: "won", label: "Vinte" }, { key: "lost", label: "Perse" }]} />
      <SelectField label="Prossima azione" value={f.next_action} onChange={(v) => set({ next_action: v })} allowNone="Indifferente" testId="opp-filter-next-action"
        options={[{ key: "overdue", label: "Scaduta" }, { key: "today", label: "Oggi" }, { key: "upcoming", label: "Prossima" }, { key: "none", label: "Assente" }]} />
      <SelectField label="Fonte" value={f.source} onChange={(v) => set({ source: v })} options={lk.lead_source} allowNone="Tutte" testId="opp-filter-source" />
      <div><Label className="text-xs">Valore (€)</Label><div className="mt-1.5 grid grid-cols-2 gap-2">
        <Input type="number" min="0" placeholder="Da" value={f.value_min} onChange={(e) => set({ value_min: e.target.value })} data-testid="opp-filter-value-min" />
        <Input type="number" min="0" placeholder="A" value={f.value_max} onChange={(e) => set({ value_max: e.target.value })} data-testid="opp-filter-value-max" /></div></div>
      <div><Label className="text-xs">Chiusura prevista</Label><div className="mt-1.5 grid grid-cols-2 gap-2">
        <Input type="date" value={f.close_from} onChange={(e) => set({ close_from: e.target.value })} data-testid="opp-filter-close-from" />
        <Input type="date" value={f.close_to} onChange={(e) => set({ close_to: e.target.value })} data-testid="opp-filter-close-to" /></div></div>
      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.archived} onChange={(e) => set({ archived: e.target.checked })} data-testid="opp-filter-archived" />Mostra archiviate</label>
    </PopoverContent>
  </Popover>
);

export default function OpportunitiesPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const lk = useLookups();
  const users = useAssignees();
  const { stages } = usePipeline();
  const [view, setView] = useState(() => localStorage.getItem("onsuite.opp.view") || "board");
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const set = (p) => setF((x) => ({ ...x, page: 1, ...p }));
  const switchView = (v) => { setView(v); localStorage.setItem("onsuite.opp.view", v); };

  const load = useCallback(() => {
    const req = view === "board" ? api.get("/sales/opportunities/board", { params: clean({ q: f.q, owner_id: f.owner_id }) })
      : api.get("/sales/opportunities", { params: clean(f) });
    return req.then((r) => setData({ view, ...r.data })).catch((e) => toast.error(errorMessage(e)));
  }, [f, view]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const items = data?.view === "board" ? data.columns.flatMap((c) => c.items) : data?.items || [];
  return (
    <div data-testid="opportunities-page">
      <PageHeader overline="Sales" title="Opportunità" description="La pipeline commerciale: dove siamo, quanto vale, cosa fare adesso."
        actions={can("opportunities:create") && <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="opp-new-button"><Plus />Nuova opportunità</Button>} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca opportunità…" value={f.q} onChange={(e) => set({ q: e.target.value })} data-testid="opp-search" /></div>
        <SelectField value={f.owner_id} onChange={(v) => set({ owner_id: v })} options={users} allowNone="Tutti i responsabili" testId="opp-filter-owner" className="sm:w-56" />
        {view === "list" && <ListFilters f={f} set={set} lk={lk} stages={stages} />}
        <div className="inline-flex rounded-control border border-line bg-white p-0.5" data-testid="opp-view-toggle">
          {[["board", "Kanban", Columns3], ["list", "Lista", List]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => switchView(k)} className={`inline-flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-sm ${view === k ? "bg-brand-50 font-semibold text-ink" : "text-slate-500 hover:text-ink"}`} data-testid={`opp-view-${k}`}><Icon className="h-4 w-4" />{l}</button>
          ))}
        </div>
      </div>
      {data?.view === "board" && <PipelineBoard columns={data.columns} canMove={can("opportunities:update")} onChanged={load} />}
      {data?.view === "list" && <>
        <Panel className="overflow-x-auto"><OpportunityTable items={data.items} f={f} set={set} />
          {data.items.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="opp-empty">Nessuna opportunità trovata.</p>}</Panel>
        <Pager page={data.page} pages={data.pages} total={data.total} unit="opportunità" onPage={(p) => setF((x) => ({ ...x, page: p }))} />
      </>}
      {items.some((o) => o.is_demo) && <DemoBadge className="mt-3" />}
      <OpportunityDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={(id) => nav(`/opportunita/${id}`)} />
    </div>
  );
}
